import { useTheme } from 'emotion-theming'
import PropTypes from 'prop-types'
import React from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { compose, getContext } from 'recompose'

import {
  ContrastIcon,
  FontSizeIcon,
} from '../../../src/components/elements/Icons'
import styled, {
  focusNoneStyle,
  FONT_SIZES,
  MQ_TABLET,
  Theme,
} from '../../../src/constants/theme'
import GuiQuery from '../../../src/graphql/client/GuiQuery.gql'
import UpdateGuiMutation from '../../../src/graphql/client/UpdateGuiMutation.gql'
import {
  toggleFontSize,
  toggleHighContrast,
} from '../../../src/helpers/accessibility'
import FooterDiv from '../elements/FooterDiv'

const Layout = ({ children, setTheme }) => {
  const {
    data: {
      gui: { fontSize, isHighContrast },
    },
  }: any = useQuery(GuiQuery)
  const [updateGui] = useMutation(UpdateGuiMutation)
  const theme: Theme = useTheme()

  React.useEffect(() => {
    // this is what remembers font size from local storage
    toggleFontSize(updateGui, fontSize, null)
    setDarkBg()
  }, [isHighContrast]) // eslint-disable-line

  const setDarkBg = () => {
    document.body.style.backgroundColor = theme.primaryDark
  }

  const handleToggleFontSize = () => {
    const newSize = Number(fontSize) + 1
    toggleFontSize(updateGui, newSize, 'global')
  }

  const handleToggleHighContrast = () => {
    toggleHighContrast(updateGui, setTheme, isHighContrast, 'global')
  }

  const terms_and_agreements = (
    <React.Fragment>
      {(window as any).CMD.config.tenant.informed_consent && (
        <React.Fragment>
          {', '}
          <a
            href={(window as any).CMD.config.tenant.informed_consent}
            rel="noopener noreferrer"
            target="_blank"
          >
            Informed Consent
          </a>
        </React.Fragment>
      )}
      {(window as any).CMD.config.tenant.notice_of_privacy_practices && (
        <React.Fragment>
          {' and '}
          <a
            href={(window as any).CMD.config.tenant.notice_of_privacy_practices}
            rel="noopener noreferrer"
            target="_blank"
          >
            Notice of Privacy Practices
          </a>
        </React.Fragment>
      )}
      {(window as any).CMD.config.tenant.patient_financial_responsibility && (
        <React.Fragment>
          {' and '}
          <a
            href={
              (window as any).CMD.config.tenant.patient_financial_responsibility
            }
            rel="noopener noreferrer"
            target="_blank"
          >
            Patient Financial Responsibility Agreement
          </a>
        </React.Fragment>
      )}
    </React.Fragment>
  )

  const contrastTitle = isHighContrast
    ? 'Decrease contrast'
    : 'Increase contrast'
  let fontTitle = 'Increase font size'
  if (fontSize !== undefined) {
    const numFontSize = Number(fontSize)
    const nextFontSize =
      numFontSize === FONT_SIZES.length - 1 ? 0 : numFontSize + 1
    const nextFontTitle = FONT_SIZES[nextFontSize].label
    fontTitle += ` ${nextFontTitle}`
  }

  return (
    <Wrapper>
      <BgImg />
      <A11yControls>
        <button
          aria-live="polite"
          className={isHighContrast ? 'active' : undefined}
          css={focusNoneStyle}
          onClick={handleToggleHighContrast}
          title={contrastTitle}
        >
          <ContrastSpan tabIndex={-1}>
            <ContrastIcon />
          </ContrastSpan>
        </button>
        <button
          aria-live="polite"
          css={focusNoneStyle}
          onClick={handleToggleFontSize}
          title={fontTitle}
        >
          <FontSizeSpan tabIndex={-1}>
            <FontSizeIcon />
          </FontSizeSpan>
        </button>
      </A11yControls>
      <LogoImg alt={``} src={(window as any).CMD.config.tenant.primary_logo} />
      {children}
      <FooterDiv>
        By registering or signing in, you agree to our{' '}
        <a
          href={(window as any).CMD.config.tenant.terms_of_service}
          rel="noopener noreferrer"
          target="_blank"
        >
          Terms of Use
        </a>
        {(window as any).CMD.config.tenant.privacy_policy && (
          <React.Fragment>
            {' and '}
            <a
              href={(window as any).CMD.config.tenant.privacy_policy}
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
          </React.Fragment>
        )}
        {(window as any).CMD.config.tenant.terms_and_agreements_enabled &&
          terms_and_agreements}
      </FooterDiv>
      {(window as any).CMD.config.key !== 'cirrusmd' && (
        <PoweredByDiv>
          <img alt={``} src={(window as any).CMD.config.cirrus_logo} />
        </PoweredByDiv>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div(
  {
    '.modal-open &': {
      filter: 'blur(5px)',
    },
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  },
  ({ theme }) => ({
    ...MQ_TABLET({
      padding: theme.padding,
    }),
    padding: theme.paddingMobile,
  }),
)

const BgImg = styled.div(
  {
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    left: 0,
    minHeight: 800,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: -1,
  },
  ({ theme }) => ({
    backgroundImage: `linear-gradient(${
      (window as any).CMD.config.tenant.primary_color_opacity
    }, ${theme.primaryDark} 85%), url(${
      (window as any).CMD.config.tenant.background_image
    })`,
  }),
)

const A11yControls = styled.div(
  {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 6,
    position: 'absolute',
  },
  ({ theme }) => ({
    ...MQ_TABLET({
      right: theme.padding,
      top: theme.padding,
    }),
    '& svg': {
      fill: theme.background,
    },
    right: theme.paddingMobile,
    top: theme.paddingMobile,
  }),
)

const ContrastSpan = styled.span(
  {
    '& svg': {
      marginTop: 3,
    },
    '&:focus': {
      border: 'none',
      outline: 'none',
    },
    'button.active & svg': {
      transform: 'scaleX(-1)',
    },
    padding: 10,
  },
  ({ theme }) => ({
    'button:focus &': {
      border: `2px groove ${theme.primary}`,
    },
  }),
)

const FontSizeSpan = styled.span(
  {
    '&:focus': {
      border: 'none',
      outline: 'none',
    },
    padding: '12px 2px',
  },
  ({ theme }) => ({
    'button:focus &': {
      border: `2px groove ${theme.primary}`,
    },
  }),
)

const LogoImg = styled.img({
  ...MQ_TABLET({
    margin: '60px 0 40px',
  }),
  margin: '40px 0 20px',
  maxWidth: 300,
})

const PoweredByDiv = styled.div(
  {
    '& img': {
      maxWidth: '100%',
      width: 180,
    },
    '&:before': {
      content: '"Powered By"',
      display: 'block',
      fontSize: 13,
      position: 'relative',
      textTransform: 'uppercase',
      top: 5,
    },
    position: 'relative',
    textAlign: 'center',
  },
  ({ theme }) => ({
    '&:before': {
      color: theme.textLight,
    },
  }),
)

export default compose<any, any>(
  getContext({
    setTheme: PropTypes.func,
  }),
)(Layout)
